/**
 * 정산 시트 파서 — 레거시 `settlementSheetParser.js` 포팅.
 * 템플릿 미매칭 시 범용 폴백 없이 unmatched 처리 (레거시와 동일).
 */
import { extractWorkbookHeaderProfilesFromRows } from '@/pages/settlements/settlementSheetConfig';
import { resolveSettlementTemplate } from '@/pages/settlements/settlement-templates/index.js';
import {
  getFileExtension,
  sortRows,
} from '@/pages/settlements/settlement-templates/settlementTemplateHelpers.js';
import * as XLSX from 'xlsx';

export interface SettlementRow {
  pharmaceuticalCompanyName: string;
  customerName: string;
  drugCode: string;
  drugName: string;
  unitPrice: number | null;
  quantity: number | null;
  prescriptionAmount: number | null;
  feeRatePercent: number | null;
  settlementAmount: number | null;
  note: string;
  sourceFile?: string;
  sourceFileName?: string;
  id?: string;
}

export interface SettlementParseSummary {
  totalFiles: number;
  parsedFiles: number;
  unsupportedFiles: number;
  unmatchedFiles: number;
  emptyFiles: number;
  extractedRows: number;
  pharmaceuticalCompanies: number;
  counterparties: number;
}

export interface SettlementFileStatus {
  fileName: string;
  status: 'parsed' | 'unmatched' | 'unsupported' | 'error' | 'empty';
  extractedRowCount: number;
  pharmaceuticalCompanyName?: string;
  templateName?: string;
  message?: string;
}

export interface SettlementParseResult {
  generatedAt?: string;
  summary: SettlementParseSummary;
  rows: SettlementRow[];
  files: SettlementFileStatus[];
}

interface TemplateRecord {
  pharmaceuticalCompanyName?: string;
  customerName?: string;
  drugCode?: string;
  drugName?: string;
  unitPrice?: number | null;
  quantity?: number | null;
  prescriptionAmount?: number | null;
  feeRatePercent?: number | null;
  settlementAmount?: number | null;
  note?: string;
  sourceFileName?: string;
  id?: string;
}

const mapTemplateRecord = (record: TemplateRecord): SettlementRow => ({
  pharmaceuticalCompanyName: record.pharmaceuticalCompanyName ?? '',
  customerName: record.customerName ?? '',
  drugCode: record.drugCode ?? '',
  drugName: record.drugName ?? '',
  unitPrice: record.unitPrice ?? null,
  quantity: record.quantity ?? null,
  prescriptionAmount: record.prescriptionAmount ?? null,
  feeRatePercent: record.feeRatePercent ?? null,
  settlementAmount: record.settlementAmount ?? null,
  note: record.note ?? '',
  sourceFile: record.sourceFileName ?? '',
  sourceFileName: record.sourceFileName ?? '',
  id: record.id,
});

const buildFileStatus = (
  fileName: string,
  pharmaceuticalCompanyName: string,
  templateName: string,
  status: SettlementFileStatus['status'],
  extractedRowCount: number,
  message = '',
): SettlementFileStatus => ({
  fileName,
  pharmaceuticalCompanyName: pharmaceuticalCompanyName || '',
  templateName: templateName || '',
  status,
  extractedRowCount,
  message: message || undefined,
});

export const parseSettlementFiles = async (
  inputFiles: File[] = [],
): Promise<SettlementParseResult> => {
  const files = [...inputFiles].sort((left, right) =>
    left.name.localeCompare(right.name, 'ko'),
  );
  const fileStatuses: SettlementFileStatus[] = [];
  const rows: SettlementRow[] = [];

  for (const file of files) {
    const extension = getFileExtension(file.name);

    if (!['.xlsx', '.xls'].includes(extension)) {
      fileStatuses.push(
        buildFileStatus(
          file.name,
          '',
          '',
          'unsupported',
          0,
          '현재 MVP는 xls/xlsx만 지원합니다.',
        ),
      );
      continue;
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, {
        type: 'array',
        cellDates: false,
        raw: false,
      });

      const sheetRowsMap = Object.fromEntries(
        workbook.SheetNames.map((sheetName) => [
          sheetName,
          XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[sheetName], {
            header: 1,
            defval: null,
            blankrows: false,
          }),
        ]),
      );

      const headerProfiles = extractWorkbookHeaderProfilesFromRows(
        workbook,
        sheetRowsMap,
      );
      const templateContext = {
        file,
        workbook,
        sheetRowsMap,
        headerProfiles,
      };
      const matchedTemplate = resolveSettlementTemplate(templateContext);
      if (!matchedTemplate) {
        fileStatuses.push(
          buildFileStatus(
            file.name,
            '',
            '',
            'unmatched',
            0,
            '매칭되는 템플릿이 없어 처리하지 않았습니다.',
          ),
        );
        continue;
      }

      const templateResult = matchedTemplate.extract(templateContext);
      const fileRows = (templateResult.rows ?? []).map(mapTemplateRecord);

      rows.push(...fileRows);

      const companiesInRows = [
        ...new Set(
          fileRows
            .map((row) => row.pharmaceuticalCompanyName)
            .filter((value) => value),
        ),
      ];
      const filePharmaceuticalCompanyName =
        companiesInRows.length === 0
          ? (templateResult.pharmaceuticalCompanyName ?? '')
          : companiesInRows.length === 1
            ? companiesInRows[0]
            : `복수 제약사 (${companiesInRows.length})`;

      const templateName =
        templateResult.templateName ||
        matchedTemplate.name ||
        matchedTemplate.id ||
        '';

      if (fileRows.length > 0) {
        fileStatuses.push(
          buildFileStatus(
            file.name,
            filePharmaceuticalCompanyName,
            templateName,
            'parsed',
            fileRows.length,
          ),
        );
      } else {
        fileStatuses.push(
          buildFileStatus(
            file.name,
            filePharmaceuticalCompanyName,
            templateName,
            'empty',
            0,
            '표준 컬럼으로 변환할 수 있는 상세 행을 찾지 못했습니다.',
          ),
        );
      }
    } catch (error) {
      fileStatuses.push(
        buildFileStatus(
          file.name,
          '',
          '',
          'error',
          0,
          error instanceof Error ? error.message : '파일 파싱 실패',
        ),
      );
    }
  }

  const normalizedRows = sortRows(rows) as SettlementRow[];
  const parsedFiles = fileStatuses.filter(
    (fileStatus) => fileStatus.status === 'parsed',
  ).length;
  const unsupportedFiles = fileStatuses.filter(
    (fileStatus) => fileStatus.status === 'unsupported',
  ).length;

  return {
    generatedAt: new Date().toISOString(),
    summary: {
      totalFiles: fileStatuses.length,
      parsedFiles,
      unsupportedFiles,
      unmatchedFiles: fileStatuses.filter(
        (fileStatus) => fileStatus.status === 'unmatched',
      ).length,
      emptyFiles: fileStatuses.filter((fileStatus) => fileStatus.status === 'empty')
        .length,
      extractedRows: normalizedRows.length,
      pharmaceuticalCompanies: new Set(
        normalizedRows
          .map((row) => row.pharmaceuticalCompanyName)
          .filter((value) => value),
      ).size,
      counterparties: new Set(
        normalizedRows.map((row) => row.customerName).filter((value) => value),
      ).size,
    },
    files: fileStatuses,
    rows: normalizedRows,
  };
};

/** 레거시 settlementExportHeaderMap — sourceFile 제외 */
export const SETTLEMENT_EXPORT_HEADER_MAP: Record<string, string> = {
  pharmaceuticalCompanyName: '제약사명',
  customerName: '병의원명',
  drugCode: '의약품 코드',
  drugName: '의약품명',
  unitPrice: '단가',
  quantity: '처방수량',
  prescriptionAmount: '처방금액',
  feeRatePercent: '정산수수료율',
  settlementAmount: '정산금액',
  note: '비고',
};
